/**
 * BFF Service - Cliente Frontend Seguro para o Backend for Frontend (Amplify API Gateway + Lambda)
 * 
 * Centraliza as requisições seguras, eliminando a necessidade de expor
 * AUTH_BASE_SECRET, INSURANCE_API_KEY, SENSEDIA_SECRETID e URLs de SQS no frontend.
 */

// URL base do BFF (configurada via Amplify ou endpoint do API Gateway)
const BFF_BASE_URL = process.env.REACT_APP_BFF_API_URL || 
                     'https://vpytjjfrusp6c54x2hrumxovdm0zmhpw.lambda-url.us-east-1.on.aws';

/**
 * Utilitário genérico de requisição para o BFF
 */
async function callBff(path, options = {}) {
    const url = `${BFF_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
    
    const headers = {
        'Content-Type': 'application/json',
        ...(options.headers || {})
    };

    const response = await fetch(url, {
        ...options,
        headers
    });

    if (!response.ok) {
        let errorData = null;
        try {
            errorData = await response.json();
        } catch (e) {
            errorData = { statusText: response.statusText };
        }
        const error = new Error(errorData.error || `Erro HTTP ${response.status} na chamada BFF`);
        error.status = response.status;
        error.data = errorData;
        throw error;
    }

    const text = await response.text();
    if (!text || text.trim() === '') {
        return {};
    }

    try {
        return JSON.parse(text);
    } catch (e) {
        return { raw: text };
    }
}

/**
 * 1. Obtém o código de autenticação (BFF injeta token seguro no backend)
 */
export async function bffGetAuthCode() {
    const data = await callBff('/api/auth/code', {
        method: 'GET'
    });
    return data.code;
}

/**
 * 2. Obtém o token de autenticação
 * O frontend envia apenas o code recebido. O BFF injeta o AUTH_BASE_SECRET de forma protegida.
 */
export async function bffGetAuthToken(code) {
    return await callBff('/api/auth/token', {
        method: 'POST',
        body: JSON.stringify({ code })
    });
}

/**
 * 3. Executa ações de Seguros / Convênios (ex: busca convênios, pre-signed URLs, validações)
 * O BFF injeta a INSURANCE_API_KEY no backend de forma segura.
 */
export async function bffExecuteInsuranceAction(context, code_execute = '') {
    return await callBff('/api/insurance/execute', {
        method: 'POST',
        body: JSON.stringify({ context, code_execute })
    });
}

/**
 * 4. Registra clique em link Sensedia
 * O BFF injeta SENSEDIA_CLIENTID e SENSEDIA_SECRETID protegidos.
 */
export async function bffRegisterLink(client_id, link, conversationId, origin) {
    return await callBff('/api/links/register', {
        method: 'POST',
        body: JSON.stringify({ client_id, link, conversationId, origin })
    });
}

/**
 * 5. Envia mensagem segura para o SQS
 * O BFF publica na fila AWS sem expor a URL da fila no client.
 */
export async function bffDispatchSqs(messagePayload) {
    return await callBff('/api/sqs/dispatch', {
        method: 'POST',
        body: JSON.stringify(messagePayload)
    });
}
